import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { 
  Volume2, 
  Settings as SettingsIcon, 
  Clock, 
  ChevronRight, 
  User, 
  Play, 
  Tv,
  RefreshCw,
  X
} from 'lucide-react';
import './PatientsCaller.css';

const DEFAULT_OFFICES = [
  'CONSULTORIOS EXTERNOS',
  'ECOGRAFIAS .',
  'FONOAUDIOLOGA .',
  'NEUMONOLOGO .',
  'NEUROLOGO .',
  'OTORRINO .',
  'PSICOLOGIA .',
  'RAYOS X'
];

const getLocalYYYYMMDD = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const PatientsCaller = () => {
  const [searchParams] = useSearchParams();
  
  // States
  const [filterProf, setFilterProf] = useState(() => {
    const urlParam = searchParams.get('professional');
    if (urlParam) return urlParam;
    return localStorage.getItem('gdt_caller_filter') || 'TODOS';
  });
  
  const [activeCall, setActiveCall] = useState(null);
  const [recentCalls, setRecentCalls] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const audioCtxRef = useRef(null);

  // System states
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isFlashing, setIsFlashing] = useState(false);
  const [audioUnlocked, setAudioUnlocked] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  
  // TTS configuration
  const [voices, setVoices] = useState([]);
  const [selectedVoiceName, setSelectedVoiceName] = useState(() => {
    return localStorage.getItem('gdt_caller_voice') || '';
  });

  // Reference to prevent multiple triggers
  const lastProcessedRef = useRef({ id: null, timestamp: 0 });

  // 1. Digital Clock effect
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // 2. Load SpeechSynthesis voices
  useEffect(() => {
    const loadVoices = () => {
      if (!window.speechSynthesis) return;
      const allVoices = window.speechSynthesis.getVoices();
      // Filter for Spanish and English voices primarily
      const filtered = allVoices.filter(v => v.lang.startsWith('es') || v.lang.startsWith('en'));
      setVoices(filtered);
      
      // If no voice is selected yet, choose a default Spanish voice
      if (!selectedVoiceName && filtered.length > 0) {
        const defaultSpanish = filtered.find(v => v.lang === 'es-AR') || 
                               filtered.find(v => v.lang === 'es-ES') || 
                               filtered.find(v => v.lang === 'es-MX') ||
                               filtered.find(v => v.lang.startsWith('es'));
        if (defaultSpanish) {
          setSelectedVoiceName(defaultSpanish.name);
          localStorage.setItem('gdt_caller_voice', defaultSpanish.name);
        }
      }
    };

    if (window.speechSynthesis) {
      window.speechSynthesis.onvoiceschanged = loadVoices;
      loadVoices();
    }

    return () => {
      if (window.speechSynthesis) {
        window.speechSynthesis.onvoiceschanged = null;
      }
    };
  }, [selectedVoiceName]);

  // 3. Fetch professionals list for filter config
  useEffect(() => {
    const fetchProfessionals = async () => {
      try {
        const { data, error } = await supabase
          .from('professionals')
          .select('name, full_name')
          .order('full_name', { ascending: true });
        
        if (error) throw error;
        
        if (data && data.length > 0) {
          // In case column names vary
          const list = data.map(p => p.full_name || p.name);
          setProfessionals(list);
        } else {
          setProfessionals(DEFAULT_OFFICES);
        }
      } catch (err) {
        console.error("Error fetching professionals for caller:", err);
        setProfessionals(DEFAULT_OFFICES);
      }
    };
    fetchProfessionals();
  }, []);

  // 4. Synthesized Audio Chime (Ding-Dong) using Web Audio API
  const playChime = () => {
    try {
      let ctx = audioCtxRef.current;
      if (!ctx) {
        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
        if (!AudioContextClass) return;
        ctx = new AudioContextClass();
        audioCtxRef.current = ctx;
      }
      
      const startOscillators = () => {
        // Tone 1: G5 (783.99 Hz)
        const osc1 = ctx.createOscillator();
        const gain1 = ctx.createGain();
        osc1.connect(gain1);
        gain1.connect(ctx.destination);
        osc1.type = 'sine';
        osc1.frequency.setValueAtTime(783.99, ctx.currentTime);
        gain1.gain.setValueAtTime(0.2, ctx.currentTime);
        gain1.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.6);
        osc1.start(ctx.currentTime);
        osc1.stop(ctx.currentTime + 0.6);
        
        // Tone 2: E5 (659.25 Hz) slightly delayed
        const osc2 = ctx.createOscillator();
        const gain2 = ctx.createGain();
        osc2.connect(gain2);
        gain2.connect(ctx.destination);
        osc2.type = 'sine';
        osc2.frequency.setValueAtTime(659.25, ctx.currentTime + 0.3);
        gain2.gain.setValueAtTime(0, ctx.currentTime);
        gain2.gain.setValueAtTime(0.2, ctx.currentTime + 0.3);
        gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 1.2);
        osc2.start(ctx.currentTime + 0.3);
        osc2.stop(ctx.currentTime + 1.2);
      };

      if (ctx.state === 'suspended') {
        ctx.resume().then(() => {
          startOscillators();
        }).catch(err => {
          console.error("Failed to resume context:", err);
          startOscillators();
        });
      } else {
        startOscillators();
      }
    } catch (err) {
      console.error("Failed to generate chime sound:", err);
    }
  };

  // 5. Speech Synthesis voice announcer
  const speakText = useCallback((patientName, professionalName) => {
    if (!window.speechSynthesis) return;
    
    // Clean text announcements (prevent raw formatting symbols)
    const cleanPatient = patientName.replace(/[^\w\sñáéíóú]/gi, '');
    const cleanProf = professionalName.replace(/[^\w\sñáéíóú]/gi, '').replace(/\s+\./g, '');
    
    // Formulate announcement message
    const announcement = `Paciente ${cleanPatient}, por favor dirigirse a ${cleanProf}.`;
    
    window.speechSynthesis.cancel(); // Abort previous speech if any
    const utterance = new SpeechSynthesisUtterance(announcement);
    
    // Select configured voice or fallback safely to any Spanish voice
    const voicesList = window.speechSynthesis.getVoices();
    let voice = null;
    if (selectedVoiceName) {
      voice = voicesList.find(v => v.name === selectedVoiceName);
    }
    if (!voice && voicesList.length > 0) {
      voice = voicesList.find(v => v.lang.startsWith('es'));
    }
    
    if (voice) {
      utterance.voice = voice;
      utterance.lang = voice.lang;
    } else {
      utterance.lang = 'es-ES';
    }
    
    utterance.rate = 0.9;  // Slightly slow for clinic acoustics
    utterance.pitch = 1.0;
    
    // 50ms delay after cancel() ensures Chromium's speech engine releases properly
    setTimeout(() => {
      window.speechSynthesis.speak(utterance);
    }, 50);
  }, [selectedVoiceName]);

  // 6. Trigger full notification chain
  const triggerNotification = useCallback((patientName, professionalName) => {
    setIsFlashing(true);
    // Visual flash animation lasts 6 seconds
    setTimeout(() => setIsFlashing(false), 6000);
    
    // 1. Play chime sound
    playChime();
    
    // 2. Announce via TTS after chime (approx 1.2 seconds delay)
    setTimeout(() => {
      speakText(patientName, professionalName);
    }, 1200);
  }, [speakText]);

  // Manual tests in config panel
  const handleTestAudio = () => {
    unlockAudio();
    playChime();
  };

  const handleTestVoice = () => {
    unlockAudio();
    speakText("Juan Pérez", filterProf === 'TODOS' ? "Consultorios Externos" : filterProf);
  };

  // Audio activation click handler (browser autoplay requirement)
  const unlockAudio = () => {
    if (audioUnlocked) return;
    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (AudioContextClass) {
        const ctx = new AudioContextClass();
        audioCtxRef.current = ctx;
        if (ctx.state === 'suspended') {
          ctx.resume();
        }
        console.log("AudioContext initialized and unlocked:", ctx.state);
      }
      setAudioUnlocked(true);
    } catch (e) {
      console.error(e);
    }
  };

  // 7. Load Initial called appointments
  const fetchInitialCalls = useCallback(async () => {
    setLoading(true);
    try {
      const todayStr = getLocalYYYYMMDD();
      let query = supabase
        .from('appointments')
        .select('*')
        .eq('appointment_date', todayStr)
        .in('status', ['en-llamado', 'en-consultorio', 'atendido']);

      if (filterProf !== 'TODOS') {
        query = query.eq('professional_name', filterProf);
      }

      // Order by updated_at desc
      query = query.order('updated_at', { ascending: false }).limit(10);

      const { data, error } = await query;
      if (error) throw error;

      if (data && data.length > 0) {
        const active = data.find(c => c.status === 'en-llamado');
        const recent = data.filter(c => c.id !== active?.id).slice(0, 4);
        
        setActiveCall(active || null);
        setRecentCalls(recent);
        
        if (active) {
          // Save initial state tracker to prevent re-playing sound on load
          lastProcessedRef.current = { 
            id: active.id, 
            timestamp: new Date(active.updated_at || active.created_at).getTime() 
          };
        }
      } else {
        setActiveCall(null);
        setRecentCalls([]);
      }
    } catch (err) {
      console.error("Error loading initial waitlist:", err);
    } finally {
      setLoading(false);
    }
  }, [filterProf]);

  // Reload when filter changes
  useEffect(() => {
    Promise.resolve().then(() => {
      fetchInitialCalls();
    });
  }, [fetchInitialCalls]);

  // 8. Real-time Supabase Channel Listener
  useEffect(() => {
    const todayStr = getLocalYYYYMMDD();

    const channel = supabase
      .channel('tv-waiting-room-calls')
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'appointments'
      }, (payload) => {
        const newRecord = payload.new;
        
        // 1. Match current date
        if (newRecord.appointment_date !== todayStr) return;
        
        // 2. Match professional filter
        if (filterProf !== 'TODOS' && newRecord.professional_name !== filterProf) return;
        
        // If status changed to en-llamado, trigger notification
        if (newRecord.status === 'en-llamado') {
          const updateTime = new Date(newRecord.updated_at || newRecord.created_at).getTime();
          const lastProcessed = lastProcessedRef.current;
          const timeDiff = updateTime - lastProcessed.timestamp;

          if (newRecord.id !== lastProcessed.id || timeDiff > 1500) {
            lastProcessedRef.current = { id: newRecord.id, timestamp: updateTime };
            
            // Trigger Chime + TTS voice
            triggerNotification(newRecord.patient_name, newRecord.consultorio_name || newRecord.professional_name || 'Consultorio');
            
            // Update visual states
            setActiveCall(currentActive => {
              if (currentActive && currentActive.id !== newRecord.id) {
                setRecentCalls(prev => {
                  const filtered = prev.filter(c => c.id !== currentActive.id && c.id !== newRecord.id);
                  return [currentActive, ...filtered].slice(0, 4);
                });
              }
              return newRecord;
            });
          }
        } 
        // If status changed to en-consultorio or atendido, transition from active to history
        else if (newRecord.status === 'en-consultorio' || newRecord.status === 'atendido') {
          setActiveCall(currentActive => {
            if (currentActive && currentActive.id === newRecord.id) {
              setRecentCalls(prev => {
                const filtered = prev.filter(c => c.id !== newRecord.id);
                return [newRecord, ...filtered].slice(0, 4);
              });
              return null;
            }
            // Update status inside history if it's already there
            setRecentCalls(prev => {
              const exists = prev.some(c => c.id === newRecord.id);
              if (exists) {
                return prev.map(c => c.id === newRecord.id ? newRecord : c);
              }
              const filtered = prev.filter(c => c.id !== newRecord.id);
              return [newRecord, ...filtered].slice(0, 4);
            });
            return currentActive;
          });
        }
        // If status changed to something else (e.g. pendiente, cancelado), remove from active and history
        else {
          setActiveCall(currentActive => {
            if (currentActive && currentActive.id === newRecord.id) {
              return null;
            }
            return currentActive;
          });
          setRecentCalls(prev => prev.filter(c => c.id !== newRecord.id));
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [filterProf, triggerNotification]);

  // Handle filter changes from config panel
  const handleFilterChange = (val) => {
    setFilterProf(val);
    localStorage.setItem('gdt_caller_filter', val);
  };

  // Handle voice changes
  const handleVoiceChange = (e) => {
    const val = e.target.value;
    setSelectedVoiceName(val);
    localStorage.setItem('gdt_caller_voice', val);
  };

  // Utility styling mapping for professional modules
  const getProfColor = (name) => {
    const colors = {
      'ADMINISTRACION GDT': '#009688',
      'CONSULTORIOS EXTERNOS': '#7e57c2',
      'ECOGRAFIAS .': '#ffee58',
      'FONOAUDIOLOGA .': '#ffa726',
      'NEUMONOLOGO .': '#fdd835',
      'NEUROLOGO .': '#d4e157',
      'OTORRINO .': '#66bb6a',
      'PSICOLOGIA .': '#d4e157',
      'RAYOS X': '#26c6da'
    };
    return colors[name] || '#00e5ff';
  };

  // Format HH:MM from date string/timestamp
  const formatCallTime = (timeStr) => {
    if (!timeStr) return '--:--';
    // If it's a Postgres time object e.g. "14:35:00"
    if (timeStr.includes(':') && timeStr.length <= 8) {
      return timeStr.substring(0, 5);
    }
    // If it's a full ISO timestamp
    try {
      const d = new Date(timeStr);
      return d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false });
    } catch {
      return timeStr.substring(0, 5);
    }
  };

  return (
    <div className={`caller-tv-container ${isFlashing ? 'flashing-active' : ''}`} onClick={unlockAudio}>
      {/* 1. Autoplay Unlock Overlay Banner */}
      {!audioUnlocked && (
        <div className="audio-unlock-overlay animate-fade-in">
          <div className="unlock-banner">
            <Volume2 className="pulse-icon" size={48} />
            <h2>Audio en Espera de Activación</h2>
            <p>Haga clic en cualquier parte de la pantalla para activar los sonidos y la síntesis de voz.</p>
            <button className="btn-unlock-audio" onClick={unlockAudio}>ACTIVAR SONIDO</button>
          </div>
        </div>
      )}

      {/* 2. Top Navigation Bar (Header) */}
      <header className="caller-header glass-header">
        <div className="header-brand">
          <div className="tv-icon-wrap">
            <Tv size={28} className="icon-blue" />
          </div>
          <div className="brand-titles">
            <h1>LLAMADOR DE PACIENTES</h1>
            <span className="subtitle">
              Módulo: <strong style={{ color: getProfColor(filterProf) }}>{filterProf}</strong>
            </span>
          </div>
        </div>
        
        <div className="header-status-clock">
          <div className="date-display">
            {currentTime.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' }).toUpperCase()}
          </div>
          <div className="time-display-wrapper">
            <Clock size={24} className="icon-blue" />
            <span className="digital-time">
              {currentTime.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}
            </span>
          </div>
          <button className="btn-toggle-config" onClick={() => setShowConfig(!showConfig)}>
            <SettingsIcon size={24} />
          </button>
        </div>
      </header>

      {/* 3. Settings Panel overlay */}
      {showConfig && (
        <div className="config-drawer-overlay">
          <div className="config-drawer glass-panel-premium">
            <div className="config-drawer-header">
              <h3>Configuración de Pantalla</h3>
              <button className="btn-close-drawer" onClick={() => setShowConfig(false)}><X size={20} /></button>
            </div>
            
            <div className="config-form-group">
              <label>Filtrar por Especialidad / Módulo</label>
              <select value={filterProf} onChange={(e) => handleFilterChange(e.target.value)}>
                <option value="TODOS">TODOS LOS MÓDULOS (General)</option>
                {professionals.map((prof, idx) => (
                  <option key={idx} value={prof}>{prof}</option>
                ))}
              </select>
            </div>

            <div className="config-form-group">
              <label>Voz para Lectura en Español</label>
              <select value={selectedVoiceName} onChange={handleVoiceChange}>
                {voices.length === 0 ? (
                  <option value="">Por defecto del sistema...</option>
                ) : (
                  voices.map((v, idx) => (
                    <option key={idx} value={v.name}>{v.name} ({v.lang})</option>
                  ))
                )}
              </select>
            </div>

            <div className="config-test-actions">
              <button className="btn-test-action" onClick={handleTestAudio}>
                <Play size={14} /> Probar Timbre (Chime)
              </button>
              <button className="btn-test-action" onClick={handleTestVoice}>
                <Volume2 size={14} /> Probar Voz (TTS)
              </button>
            </div>

            <div className="config-tips">
              <p>💡 <strong>Tip:</strong> Ponga el navegador en pantalla completa (tecla F11) para una experiencia óptima de televisor.</p>
            </div>
          </div>
        </div>
      )}

      {/* 4. Main content Area */}
      {loading ? (
        <div className="caller-loading-screen">
          <RefreshCw className="spinner" size={48} />
          <p>Conectando con la base de datos de la clínica...</p>
        </div>
      ) : (
        <main className="caller-main-grid">
          {/* A. Active Call Panel (Left Side - 70%) */}
          <section className="active-call-section">
            <div className="active-call-card glass-panel-premium">
              {activeCall ? (
                <div className="active-call-content">
                  <div className="active-call-label animate-pulse">
                    <span className="live-dot"></span> LLAMANDO AHORA
                  </div>
                  
                  <div className="active-patient-name">
                    {activeCall.patient_name}
                  </div>
                  
                  <div className="active-destination-arrow">
                    <ChevronRight size={64} className="arrow-animated" />
                  </div>

                  <div 
                    className="active-destination-room" 
                    style={{ 
                      borderColor: `${getProfColor(activeCall.professional_name)}aa`,
                      boxShadow: `0 8px 32px ${getProfColor(activeCall.professional_name)}33`
                    }}
                  >
                    <div className="room-icon" style={{ backgroundColor: getProfColor(activeCall.professional_name) }}>
                      <User size={32} />
                    </div>
                    <div className="room-details">
                      <span className="room-title">POR FAVOR DIRIGIRSE A:</span>
                      <span className="room-name" style={{ color: getProfColor(activeCall.professional_name) }}>
                        {activeCall.consultorio_name || activeCall.professional_name?.replace(/\s+\./g, '')}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="no-active-calls">
                  <Tv size={120} className="empty-tv-icon" />
                  <h2>Pantalla Lista para Sala de Espera</h2>
                  <p>En espera de llamados de pacientes por parte de los profesionales médicos.</p>
                </div>
              )}
            </div>
          </section>

          {/* B. Recent Calls Panel (Right Side - 30%) */}
          <section className="recent-calls-section">
            <div className="recent-calls-container glass-panel-premium">
              <h2 className="recent-calls-title">LLAMADOS RECIENTES</h2>
              
              <div className="recent-calls-list">
                {recentCalls.length === 0 ? (
                  <div className="empty-recent">
                    <p>No hay llamados previos registrados hoy.</p>
                  </div>
                ) : (
                  recentCalls.map((call, idx) => (
                    <div key={call.id || idx} className="recent-call-item animate-slide-in">
                      <div className="recent-time">
                        {formatCallTime(call.updated_at || call.appointment_time)}
                      </div>
                      <div className="recent-body">
                        <div className="recent-patient">{call.patient_name}</div>
                        <div 
                          className="recent-room" 
                          style={{ color: getProfColor(call.professional_name) }}
                        >
                          {call.consultorio_name || call.professional_name?.replace(/\s+\./g, '')}
                        </div>
                      </div>
                      <div 
                        className="recent-color-indicator" 
                        style={{ backgroundColor: getProfColor(call.professional_name) }}
                      ></div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </section>
        </main>
      )}
    </div>
  );
};

export default PatientsCaller;
